import { Alert, IconButton, Stack, TextField, Tooltip } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import type { GridColDef } from '@mui/x-data-grid';
import { CrudPage } from '../../shared/components/CrudPage';
import { resources } from '../../core/services/resources';
import { useTenant } from '../../core/contexts/TenantContext';
import { useLineOfBiz } from '../../core/contexts/LineOfBizContext';
import { LineOfBizSelect } from '../lines-of-business/LineOfBizSelect';
import type { PromptSet } from '../../core/types/data-models';

/**
 * Right-aligned numeric column with a thousands-separator formatter; the
 * aggregate columns share this shape so they all line up neatly.
 */
const countColumn = (field: string, headerName: string): GridColDef => ({
  field,
  headerName,
  width: 130,
  type: 'number',
  align: 'right',
  headerAlign: 'right',
  valueFormatter: (v: unknown) => (typeof v === 'number' ? v.toLocaleString() : (v ?? '') as string),
});

const columns: GridColDef[] = [
  { field: 'id', headerName: 'ID', width: 80 },
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    minWidth: 220,
    renderCell: (p) => (
      <RouterLink to={`/prompt-sets/${p.row.id}`}>{p.value as string}</RouterLink>
    ),
  },
  { field: 'description', headerName: 'Description', flex: 2, minWidth: 240 },
  // Aggregates from the backend: count of attached prompts and count of
  // distinct FK values across those prompts' defaults.
  countColumn('promptCount', 'Prompts'),
  countColumn('categoryCount', 'Categories'),
  countColumn('personaCount', 'Personas'),
  countColumn('stageCount', 'Stages'),
  countColumn('brandCount', 'Brands'),
];

export function PromptSetsPage() {
  const navigate = useNavigate();
  const { activeTenantId } = useTenant();
  const { activeLineOfBizId } = useLineOfBiz();

  return (
    <CrudPage<PromptSet>
      title="Prompt sets"
      entityName="Prompt set"
      description="Reusable groups of prompts attached to a report template as a unit. Scoped to the active line of business."
      // Cache key includes the active LOB so the list re-fetches when the
      // user switches LOB. The list filter below scopes the actual query.
      cacheKey={['prompt-sets', { lineOfBizId: activeLineOfBizId }]}
      service={resources.promptSets}
      columns={columns}
      listQuery={
        activeLineOfBizId != null
          ? { filter: { lineOfBizId: activeLineOfBizId } }
          : { filter: {} }
      }
      // PromptSet is tenant + LOB scoped now — seed both from context so
      // the form starts in the right scope; the LineOfBizSelect below lets
      // the user override on create.
      defaultValues={{
        name: '',
        description: '',
        lineOfBizId: activeLineOfBizId ?? undefined,
        tenantId: activeTenantId ?? undefined,
      }}
      beforeSubmit={(v) => ({
        ...v,
        lineOfBizId: v.lineOfBizId ?? activeLineOfBizId ?? undefined,
        tenantId: v.tenantId ?? activeTenantId ?? undefined,
      })}
      // Extra row action — same target as clicking the set's name.
      rowActions={(row) => (
        <Tooltip title="Open / manage prompts">
          <IconButton
            size="small"
            color="primary"
            onClick={() => navigate(`/prompt-sets/${row.id}`)}
            aria-label="open prompt set"
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      renderForm={({ values, setValues }) => (
        <Stack spacing={2}>
          {activeLineOfBizId == null && (
            <Alert severity="info">
              Pick a line of business in the sidebar — every prompt set must belong to one.
            </Alert>
          )}
          <TextField
            label="Name"
            required
            value={values.name ?? ''}
            onChange={(e) => setValues({ ...values, name: e.target.value })}
          />
          <TextField
            label="Description"
            multiline
            minRows={3}
            value={values.description ?? ''}
            onChange={(e) => setValues({ ...values, description: e.target.value })}
          />
          <LineOfBizSelect
            label="Line of business"
            helperText="Required. The LOB this set belongs to."
            required
            value={values.lineOfBizId ?? null}
            onChange={(id) => setValues({ ...values, lineOfBizId: id ?? undefined })}
          />
        </Stack>
      )}
    />
  );
}
